import axios from "axios";
import { toast } from "sonner";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Timeout guard — no request should legitimately take longer than 30 s from
// the admin's browser. Cloudflare (100 s edge timeout on production) will
// otherwise bounce the tab back with a "could not parse" error page. Every
// long-running server flow (welcome pack, bulk email, invoice send etc.)
// has been moved to a FastAPI BackgroundTask so the request itself now
// returns in < 1 s — 30 s is a comfortable ceiling with room for a
// congested network round-trip.
const api = axios.create({ baseURL: API, timeout: 30000 });

api.interceptors.request.use((cfg) => {
  const token = localStorage.getItem("sac_token");
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    // Session expired / signing-key rotated — kick to login gently.
    if (err?.response?.status === 401) {
      localStorage.removeItem("sac_token");
      if (window.location.pathname.startsWith("/admin") && !window.location.pathname.endsWith("/login")) {
        try { toast.error("Your session expired — please sign in again"); } catch { /* toaster not mounted yet */ }
        window.location.href = "/admin/login";
      }
    }
    // Network / origin issue (Cloudflare 520/524, upstream down, offline).
    // These show up as either `err.code === 'ECONNABORTED'` (axios timeout)
    // or `err.response` being undefined / 5xx with no JSON body. Surface
    // a friendly toast instead of the browser's blank-page default so the
    // admin knows to retry rather than assume "save silently failed".
    if (err?.code === "ECONNABORTED") {
      try { toast.error("This request took too long — please try again"); } catch { /* noop */ }
    } else if (!err?.response) {
      try { toast.error("Can't reach the server right now — check your connection and retry"); } catch { /* noop */ }
    } else if (err.response.status >= 500 && err.response.status !== 502) {
      // 502 usually means backend restart mid-request — quiet retry expected.
      try { toast.error("Server hiccup — please try that again in a moment"); } catch { /* noop */ }
    }
    return Promise.reject(err);
  }
);

export default api;
